/**
 * Calendar Fixed Starts At required gates for MaintenanceTaskModal.
 * Shared with unit tests so predicate drift cannot hide a broken Save disable path.
 */

export const isExistingCalendarStartsAtMissing = (params: {
  isCreatingInterval: boolean
  showStartsAt: boolean
  intervalTypeId: string
  fixedScheduleStartTime: string
}): boolean =>
  !params.isCreatingInterval &&
  params.showStartsAt &&
  params.intervalTypeId !== '' &&
  params.fixedScheduleStartTime.trim() === ''

export const isNewIntervalCalendarStartsAtMissing = (params: {
  showIntervalOptions: boolean
  showStartsAt: boolean
  startsAt: string
}): boolean => params.showIntervalOptions && params.showStartsAt && params.startsAt.trim() === ''

/** True when either Calendar Starts At path is empty — used by Save disable and submit() guard. */
export const calendarStartsAtBlocksSubmit = (params: {
  existingStartsAtMissing: boolean
  startsAtMissing: boolean
}): boolean => params.existingStartsAtMissing || params.startsAtMissing

/**
 * Payload value for `fixedScheduleStartTime` in form.transform.
 * REBUILD-369 Gap 2 / AC3: cleared Starts At stays `''` — never remap empty → `00:00`
 * (intentional midnight remains pass-through).
 */
export const resolveCalendarFixedScheduleStartTime = (params: {
  isCalendarFixed: boolean
  usingNewInterval: boolean
  newIntervalStartsAt: string
  fixedScheduleStartTime: string
}): string => {
  if (!params.isCalendarFixed) return ''
  const raw = params.usingNewInterval ? params.newIntervalStartsAt : params.fixedScheduleStartTime
  return raw.trim()
}

/**
 * Initial per-task Starts At for the modal form (REBUILD-621).
 * Edit and clone both carry the source task's own value; only a task with none (or a
 * brand-new task) falls back to the legacy midnight default.
 */
export const resolveInitialFixedScheduleStartTime = (
  sourceFixedScheduleStartTime: string | null | undefined,
): string => sourceFixedScheduleStartTime || '00:00'

/**
 * Starts At after the user picks a saved Calendar frequency (REBUILD-621).
 * A frequency created with a Starts At carries it as `defaultStartTime`; selecting it fills the
 * task's own start time (the user can still edit it afterwards). A frequency without one — or a
 * non-Calendar type — leaves the current value untouched.
 */
export const resolveStartTimeOnIntervalSelect = (params: {
  frequencyTypeCode: string
  intervalDefaultStartTime: string | null | undefined
  currentStartTime: string
}): string =>
  params.frequencyTypeCode === 'CALENDAR_FIXED' && params.intervalDefaultStartTime
    ? params.intervalDefaultStartTime
    : params.currentStartTime
